// ========================================
// BLOG MANAGER
// Gestionnaire des articles de blog
// ========================================

import React, { useState } from 'react';
import AdminLayout from '../../../components/admin/Layout/AdminLayout';
import useBlogPosts from '../../../hooks/useBlogPosts';
import { ImageUploader, RichTextEditor } from '../../../components/admin/Editor';
import { slugify, formatDate, calculateReadTime } from '../../../utils';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiSearch,
  FiX,
  FiSave,
  FiLoader,
  FiArrowLeft,
  FiCalendar,
  FiClock,
  FiHeart,
  FiTag,
  FiFileText,
  FiSend,
  FiArchive
} from 'react-icons/fi';
import './BlogManager.css';

// Catégories du blog
const BLOG_CATEGORIES = [
  { value: 'communication', label: 'Communication & Médias', color: '#3B82F6' },
  { value: 'leadership', label: 'Leadership & Engagement', color: '#10B981' },
  { value: 'ecriture', label: 'Écriture & Inspiration', color: '#8B5CF6' },
  { value: 'opportunites', label: 'Opportunités', color: '#F59E0B' }
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Brouillon', icon: FiFileText },
  { value: 'published', label: 'Publié', icon: FiSend },
  { value: 'archived', label: 'Archivé', icon: FiArchive }
];

const BlogManager = () => {
  const { posts, loading, createPost, editPost, removePost } = useBlogPosts();
  
  // États
  const [view, setView] = useState('list');
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Formulaire vide
  const emptyForm = {
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    coverImage: null,
    category: 'communication',
    tags: [],
    status: 'draft',
    seo: {
      metaTitle: '',
      metaDescription: ''
    }
  };

  const [formData, setFormData] = useState(emptyForm);
  const [tagInput, setTagInput] = useState('');

  // Filtrer les articles
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || post.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || post.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Stats rapides
  const stats = {
    total: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    drafts: posts.filter(p => p.status === 'draft').length,
    totalViews: posts.reduce((sum, p) => sum + (p.views || 0), 0),
    totalLikes: posts.reduce((sum, p) => sum + (p.likes || 0), 0)
  };

  // Nouveau article
  const handleNewPost = () => {
    setFormData(emptyForm);
    setEditingPost(null);
    setView('form');
  };

  // Éditer un article
  const handleEditPost = (post) => {
    setFormData({
      ...emptyForm,
      ...post,
      seo: post.seo || { metaTitle: '', metaDescription: '' }
    });
    setEditingPost(post.id);
    setView('form');
  };

  // Mettre à jour le formulaire
  const updateFormData = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      if (field === 'title' && !editingPost) {
        updated.slug = slugify(value);
      }
      
      return updated;
    });
  };

  // Ajouter un tag
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      updateFormData('tags', [...formData.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  // Supprimer un tag
  const removeTag = (tag) => {
    updateFormData('tags', formData.tags.filter(t => t !== tag));
  };

  // Sauvegarder
  const handleSave = async (publishNow = false) => {
    if (!formData.title.trim()) return;

    setSaving(true);

    const dataToSave = {
      ...formData,
      readTime: calculateReadTime(formData.content),
      status: publishNow ? 'published' : formData.status
    };

    try {
      if (editingPost) {
        await editPost(editingPost, dataToSave);
      } else {
        await createPost(dataToSave);
      }
      setView('list');
      setFormData(emptyForm);
      setEditingPost(null);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setSaving(false);
    }
  };

  // Supprimer
  const handleDelete = async (postId) => {
    await removePost(postId);
    setShowDeleteConfirm(null);
  };

  // Annuler
  const handleCancel = () => {
    setView('list');
    setFormData(emptyForm);
    setEditingPost(null);
  };

  // Rendu du formulaire
  const renderForm = () => (
    <div className="blog-form-container">
      <div className="form-header">
        <button className="back-btn" onClick={handleCancel}>
          <FiArrowLeft />
          Retour aux articles
        </button>
        <h2>{editingPost ? 'Modifier l\'article' : 'Nouvel article'}</h2>
      </div>

      <div className="blog-form">
        {/* Colonne principale */}
        <div className="form-main">
          {/* Titre et slug */}
          <div className="form-card">
            <div className="form-group">
              <label className="form-label">Titre de l'article *</label>
              <input
                type="text"
                className="form-input title-input"
                value={formData.title}
                onChange={(e) => updateFormData('title', e.target.value)}
                placeholder="Titre accrocheur de votre article"
              />
            </div>

            <div className="form-group">
              <label className="form-label">URL de l'article</label>
              <div className="input-with-prefix">
                <span>/blog/</span>
                <input
                  type="text"
                  className="form-input"
                  value={formData.slug}
                  onChange={(e) => updateFormData('slug', slugify(e.target.value))}
                  placeholder="titre-de-larticle"
                />
              </div>
            </div>
          </div>

          {/* Image de couverture */}
          <div className="form-card">
            <h3>Image de couverture</h3>
            <ImageUploader
              value={formData.coverImage}
              onChange={(url) => updateFormData('coverImage', url)}
              folder="portfolio/blog"
              placeholder="Image de couverture (recommandé: 1200x630px)"
            />
          </div>

          {/* Extrait */}
          <div className="form-card">
            <h3>Extrait</h3>
            <textarea
              className="form-input form-textarea"
              value={formData.excerpt}
              onChange={(e) => updateFormData('excerpt', e.target.value)}
              placeholder="Résumé de l'article (affiché dans les listes et partages)"
              rows={3}
            />
            <span className="char-count">{formData.excerpt?.length || 0}/300 caractères</span>
          </div>

          {/* Contenu */}
          <div className="form-card">
            <h3>Contenu de l'article</h3>
            <RichTextEditor
              value={formData.content}
              onChange={(content) => updateFormData('content', content)}
              placeholder="Écrivez votre article ici..."
              minHeight={400}
            />
            <div className="content-stats">
              <span><FiClock /> {calculateReadTime(formData.content)} min de lecture</span>
            </div>
          </div>

          {/* SEO */}
          <div className="form-card">
            <h3>Référencement (SEO)</h3>
            
            <div className="form-group">
              <label className="form-label">Meta titre</label>
              <input
                type="text"
                className="form-input"
                value={formData.seo?.metaTitle || ''}
                onChange={(e) => updateFormData('seo', { ...formData.seo, metaTitle: e.target.value })}
                placeholder="Titre pour les moteurs de recherche"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Meta description</label>
              <textarea
                className="form-input form-textarea"
                value={formData.seo?.metaDescription || ''}
                onChange={(e) => updateFormData('seo', { ...formData.seo, metaDescription: e.target.value })}
                placeholder="Description pour les moteurs de recherche"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Colonne latérale */}
        <div className="form-sidebar">
          {/* Publication */}
          <div className="form-card">
            <h3>Publication</h3>
            
            <div className="status-selector">
              {STATUS_OPTIONS.map(opt => (
                <label 
                  key={opt.value}
                  className={`status-option ${formData.status === opt.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={opt.value}
                    checked={formData.status === opt.value}
                    onChange={(e) => updateFormData('status', e.target.value)}
                  />
                  <opt.icon />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>

            <div className="form-actions-stack">
              <button
                className="btn btn-primary btn-block"
                onClick={() => handleSave(false)}
                disabled={saving || !formData.title.trim()}
              >
                {saving ? <FiLoader className="spinner" /> : <FiSave />}
                Sauvegarder
              </button>

              {formData.status !== 'published' && (
                <button
                  className="btn btn-secondary btn-block"
                  onClick={() => handleSave(true)}
                  disabled={saving || !formData.title.trim()}
                >
                  <FiSend />
                  Publier maintenant
                </button>
              )}
            </div>
          </div>

          {/* Catégorie */}
          <div className="form-card">
            <h3>Catégorie</h3>
            
            <div className="category-list">
              {BLOG_CATEGORIES.map(cat => (
                <label 
                  key={cat.value}
                  className={`category-radio ${formData.category === cat.value ? 'selected' : ''}`}
                  style={{ '--cat-color': cat.color }}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={formData.category === cat.value}
                    onChange={(e) => updateFormData('category', e.target.value)}
                  />
                  <span className="category-dot"></span>
                  <span className="category-name">{cat.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="form-card">
            <h3><FiTag /> Tags</h3>
            
            <div className="tags-input-container">
              <input
                type="text"
                className="form-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Ajouter un tag..."
              />
              <button 
                type="button" 
                className="add-tag-btn"
                onClick={addTag}
              >
                <FiPlus />
              </button>
            </div>

            <div className="tags-list">
              {formData.tags.map(tag => (
                <span key={tag} className="tag">
                  {tag}
                  <button onClick={() => removeTag(tag)}>
                    <FiX />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendu de la liste
  const renderList = () => (
    <div className="blog-list-container">
      {/* Header */}
      <div className="list-header">
        <div className="list-header-left">
          <h1>Blog</h1>
          <span className="count-badge">{posts.length} articles</span>
        </div>

        <button className="btn btn-primary" onClick={handleNewPost}>
          <FiPlus />
          Nouvel article
        </button>
      </div>

      {/* Stats */}
      <div className="blog-stats">
        <div className="stat-item">
          <span className="stat-value">{stats.published}</span>
          <span className="stat-label">Publiés</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{stats.drafts}</span>
          <span className="stat-label">Brouillons</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{stats.totalViews}</span>
          <span className="stat-label">Vues</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{stats.totalLikes}</span>
          <span className="stat-label">Likes</span>
        </div>
      </div>

      {/* Filtres */}
      <div className="list-filters">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Rechercher un article..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <FiX />
            </button>
          )}
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">Toutes catégories</option>
            {BLOG_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Tous statuts</option>
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Liste des articles */}
      {loading ? (
        <div className="loading-state">
          <FiLoader className="spinner" />
          <p>Chargement des articles...</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="empty-state">
          <FiFileText />
          <h3>Aucun article</h3>
          <p>
            {posts.length === 0 
              ? "Commencez par créer votre premier article"
              : "Aucun article ne correspond à vos critères"
            }
          </p>
          {posts.length === 0 && (
            <button className="btn btn-primary" onClick={handleNewPost}>
              <FiPlus />
              Écrire un article
            </button>
          )}
        </div>
      ) : (
        <div className="posts-list">
          {filteredPosts.map(post => (
            <article key={post.id} className="post-card">
              <div className="post-image">
                {post.coverImage ? (
                  <img src={post.coverImage} alt={post.title} />
                ) : (
                  <div className="no-image">
                    <FiFileText />
                  </div>
                )}
              </div>

              <div className="post-content">
                <div className="post-meta">
                  <span 
                    className="category-tag"
                    style={{ backgroundColor: BLOG_CATEGORIES.find(c => c.value === post.category)?.color }}
                  >
                    {BLOG_CATEGORIES.find(c => c.value === post.category)?.label}
                  </span>
                  <span className={`status-tag ${post.status}`}>
                    {STATUS_OPTIONS.find(s => s.value === post.status)?.label}
                  </span>
                </div>

                <h3>{post.title}</h3>
                <p>{post.excerpt}</p>

                <div className="post-footer">
                  <div className="post-stats">
                    <span><FiCalendar /> {formatDate(post.createdAt)}</span>
                    <span><FiClock /> {post.readTime || 1} min</span>
                    <span><FiEye /> {post.views || 0}</span>
                    <span><FiHeart /> {post.likes || 0}</span>
                  </div>

                  <div className="post-actions">
                    <button 
                      className="action-btn"
                      onClick={() => handleEditPost(post)}
                      title="Modifier"
                    >
                      <FiEdit2 />
                    </button>
                    <a 
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-btn"
                      title="Voir"
                    >
                      <FiEye />
                    </a>
                    <button 
                      className="action-btn danger"
                      onClick={() => setShowDeleteConfirm(post.id)}
                      title="Supprimer"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Modal suppression */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon danger">
              <FiTrash2 />
            </div>
            <h3>Supprimer cet article ?</h3>
            <p>Cette action est irréversible.</p>
            <div className="modal-actions">
              <button 
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Annuler
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => handleDelete(showDeleteConfirm)}
              >
                <FiTrash2 />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div className="blog-manager">
        {view === 'form' ? renderForm() : renderList()}
      </div>
    </AdminLayout>
  );
};

export default BlogManager;